import {
  Element,
  Editor,
  Node,
  Point,
  Range,
  Text,
  Transforms,
  Path,
  type NodeEntry,
} from 'slate';
import type { CustomElement, EditorPlugin } from '../../types/aha-editor';
import removeTree from '../../util/removeTree';

const DEFAULT_BLOCK_TYPE = 'paragraph';

interface BlockFormatOptions {
  type: CustomElement['type'];
  title?: string;
  tag?: string;
  isMultiline?: boolean;
  allowTab?: boolean;
  exitOnBackspace?: boolean;
  clearFormattingAtEnd?: boolean;
  internalSoftBreak?: boolean;
  matchIndent?: boolean;
  hasAnchor?: boolean;
  hasCustomComponent?: boolean;
  skipHandleBlockSplits?: boolean;
  dependencies?: EditorPlugin[];
}

// This is a bit of a hack to make the block options available when called
// from a tool that otherwise wouldn't have access to the options.
const blockOptions: Partial<{ [key in Element['type']]: BlockFormatOptions }> =
  {};

// Utility functions for working with blocks.
class BlockUtils {
  static get blockOptions() {
    return blockOptions;
  }

  static toggleBlock(editor: Editor, blockType: string) {
    // Clear font sizes for certain block types
    if (
      [
        'paragraph',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'code',
        'quote',
      ].includes(blockType)
    ) {
      if (!editor.selection) return;

      const blockEntries: NodeEntry[] = Array.from(
        Editor.nodes(editor, {
          at: editor.selection,
          mode: 'lowest',
          match: (n) => Element.isElement(n) && Editor.isBlock(editor, n),
        })
      );

      if (blockEntries.length > 0) {
        const range = {
          anchor: Editor.start(editor, blockEntries[0][1]),
          focus: Editor.end(editor, blockEntries[blockEntries.length - 1][1]),
        };
        Transforms.unsetNodes(editor, 'fontSize', {
          at: range,
          match: (node) => Text.isText(node),
        });
      }
    }

    if (BlockUtils.selectionIsBlockType(editor, blockType)) {
      Transforms.setNodes(editor, { type: 'paragraph' });
    } else {
      BlockUtils.wrapBlocks(editor, blockType);
    }
  }

  static currentBlock(editor: Editor): NodeEntry<Element> | undefined {
    if (!editor.selection) return undefined;

    return Editor.above(editor, {
      at: editor.selection.focus,
      mode: 'lowest',
      match: (n) =>
        !Editor.isEditor(n) &&
        Element.isElement(n) &&
        Editor.isBlock(editor, n),
    });
  }

  // Return a human readable title for the current block.
  static currentBlockTitle(editor: Editor): string {
    let type: Element['type'] | undefined;
    if (editor.selection) {
      const result = Editor.above<Element>(editor, {
        at: editor.selection.focus,
        mode: 'lowest',
        match: (n) => Element.isElement(n) && Editor.isBlock(editor, n),
      });

      if (result) type = result[0].type;
    }

    if (!type || !Object.hasOwn(blockOptions, type)) {
      type = DEFAULT_BLOCK_TYPE;
    }

    const result = type
      ? blockOptions[type]?.title ?? blockOptions[type]?.type
      : undefined;

    return result ?? 'Unknown';
  }

  // Check if any of the selected nodes include the given block type.
  static selectionIsBlockType(editor: Editor, type: string) {
    const { selection } = editor;
    if (!selection) return false;

    const [match] = Array.from(
      Editor.nodes(editor, {
        at: Editor.unhangRange(editor, selection),
        match: (n) => Element.isElement(n) && n.type === type,
      })
    );

    return !!match;
  }

  // Check if the current selection is a child of the given block type.
  static selectionIsInBlock(editor: Editor, type: string) {
    if (!editor.selection) return false;

    const blockEntry = Editor.above<Element>(editor, {
      match: (n) => Element.isElement(n) && Editor.isBlock(editor, n),
    });
    if (!blockEntry) return false;
    const block = blockEntry[0];

    return block.type === type;
  }

  // Check if the selection is at the start of a block of the given type.
  static selectionIsAtStartOfBlockType(editor: Editor, type: string) {
    if (!editor.selection) return false;

    const blockEntry = Editor.above<Element>(editor, {
      match: (n) =>
        Element.isElement(n) && Editor.isBlock(editor, n) && n.type === type,
    });
    if (!blockEntry) return false;

    const startPath = Editor.start(editor, blockEntry[1]);
    return Point.equals(editor.selection.focus, startPath);
  }

  // Create new blocks above or below the current block when it is against
  // the boundary of the editor.
  static escapeBlock(
    editor: Editor,
    type: string,
    position: 'above' | 'below'
  ) {
    if (!BlockUtils.selectionIsInBlock(editor, type)) return;
    if (!editor.selection || !Range.isCollapsed(editor.selection)) return;

    if (position === 'above') {
      const textNode = Editor.previous(editor, {
        match: (n) => Text.isText(n),
      });
      if (!textNode) {
        Transforms.splitNodes(editor, { always: true });
        Transforms.move(editor, { unit: 'line', distance: 1, reverse: true });
        BlockUtils.resetBlock(editor);
      }
    } else if (position === 'below') {
      const textNode = Editor.next(editor, {
        match: (n) => Text.isText(n),
      });
      if (!textNode) {
        Transforms.splitNodes(editor, { always: true });
        Transforms.move(editor, { unit: 'line', distance: 1 });
        BlockUtils.resetBlock(editor);
      }
    }
  }

  // Convert the current block to a paragraph.
  static resetBlock(editor: Editor) {
    Transforms.setNodes(editor, { type: DEFAULT_BLOCK_TYPE });
  }

  static insertNewLine(editor: Editor, path: Path) {
    // Get the last text node in the current block.
    const [[textNode, textNodePath]] = Array.from(
      Node.texts(editor, {
        from: path,
        to: path,
        reverse: true,
      })
    );

    // Insert a new line after the text node.
    Transforms.insertText(editor, '\n', {
      at: { path: textNodePath, offset: textNode.text.length },
    });
  }

  // Wrap a set of blocks in the current type. If it's a multiline
  // type, it will convert the block to using soft breaks, and wrap
  // all of the content into a single block.
  static wrapBlocks(editor: Editor, type: string) {
    const options = blockOptions[type];
    if (!options) return;

    if (options.isMultiline || options.internalSoftBreak) {
      const blocks = Array.from(
        Editor.nodes<Element>(editor, {
          mode: 'lowest',
          match: (n) => Element.isElement(n) && Editor.isBlock(editor, n),
        })
      );

      // The first block in the selection and the one that
      // will be changed to the new type
      let parentBlock = blocks.shift() as NodeEntry<Element>;

      Editor.withoutNormalizing(editor, () => {
        // Set the type of the parent block
        Transforms.setNodes(
          editor,
          { type: options.type },
          { at: parentBlock[1] }
        );

        // Iterate over the remaining blocks.
        blocks.forEach(([, blockPath]) => {
          // Refresh the parent block.
          parentBlock = Editor.node(
            editor,
            parentBlock[1]
          ) as NodeEntry<Element>;

          // Add a new line to the parent block.
          BlockUtils.insertNewLine(editor, parentBlock[1]);

          // Get the child nodes of the current block. These nodes will be
          // be moved to the parent block.
          const children = Array.from(
            Node.children(editor, blockPath, { reverse: true })
          );

          // Iterate the children of this block, adding them
          // to the parent.
          for (let i = 0; i < children.length; i++) {
            Transforms.moveNodes(editor, {
              at: [...blockPath, 0],
              to: [...parentBlock[1], parentBlock[0].children.length + i],
            });
          }
        });

        // Go through the blocks again but in reverse order.
        // This is so that when we remove a block, we don't change the position
        // of the next one which would make the path invalid.
        blocks.reverse().forEach(([, blockPath]) => {
          removeTree(editor, blockPath);
        });
      });
    } else {
      Transforms.setNodes(editor, { type: options.type });
    }
  }

  // Return the whitespace at the start of the current line. This considers
  // the line to start after a newline character, or any block element.
  static currentLineIndent(editor: Editor): string {
    const { selection } = editor;
    if (!selection) return '';

    // Find the previous block element.
    const parentPath = Path.parent(selection.anchor.path);
    const prev = Editor.node(editor, parentPath);
    if (!prev) return '';

    // Get the text from the start of the block to the current selection.
    const str = Editor.string(editor, {
      anchor: { path: prev[1], offset: 0 },
      focus: selection.focus,
    });
    // Find the last run of whitespace in the string after a newline.
    const endMatches = str.match(/\n([ \t]*)[^\n]*$/);
    if (endMatches) return endMatches[1];

    // There is no newline, so just return the whitespace at the start of the string.
    const startMatches = str.match(/^([ \t]*).*$/);
    if (startMatches) return startMatches[1];

    return '';
  }

  static blockAnchorId(block: Element): string | undefined {
    const options = blockOptions[block.type];
    if (!options?.hasAnchor) return;

    const textContent = Node.string(block);
    if (!textContent) return;
    return this.idFromText(textContent);
  }

  static idFromText(text: string): string {
    return text.toLowerCase().trim().replace(/\W+/g, '-').replace(/-+$/, '');
  }
}

export { DEFAULT_BLOCK_TYPE };
export type { BlockFormatOptions };
export default BlockUtils;
